import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { loggedOut } from './authActions.js';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * The server answers `{ data, meta }` on success and `{ error: { message,
 * details } }` on failure. RTK Query hands the raw body to us; we reshape the
 * failure into a flat `{ status, message, details }` so that every component —
 * and `unwrap()` inside a try/catch — sees the same object it saw before Redux
 * arrived. `details` is the 422 field map that `useForm.applyServerErrors` paints
 * onto the inputs.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'include', // the session lives in httpOnly cookies
});

function normaliseError(error) {
  if (!error) return error;

  // fetchBaseQuery reports a dead server as FETCH_ERROR with no HTTP status.
  if (error.status === 'FETCH_ERROR') {
    return { status: 0, message: 'Cannot reach the server. Is the API running on port 5000?', details: undefined };
  }
  if (error.status === 'PARSING_ERROR') {
    return { status: error.originalStatus ?? 0, message: 'The server sent a response we could not read', details: undefined };
  }

  const body = error.data?.error;
  return {
    status: error.status,
    message: body?.message || `Request failed with status ${error.status}`,
    details: body?.details,
  };
}

/** A 401 from any of these *is* the answer; retrying would loop. */
const NO_RETRY = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/register'];

/**
 * One refresh at a time. A dashboard fires several queries at once, so a burst
 * of simultaneous 401s must collapse into a single call to /auth/refresh rather
 * than a stampede of them.
 */
let refreshInFlight = null;

async function refreshOnce(args, store, extraOptions) {
  if (!refreshInFlight) {
    refreshInFlight = rawBaseQuery({ url: '/auth/refresh', method: 'POST' }, store, extraOptions)
      .then((result) => !result.error)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export default async function baseQueryWithReauth(args, store, extraOptions) {
  const url = typeof args === 'string' ? args : args.url;
  let result = await rawBaseQuery(args, store, extraOptions);

  if (result.error?.status === 401 && !NO_RETRY.some((path) => url.startsWith(path))) {
    // The access token lasts fifteen minutes. Renew it and replay the request,
    // rather than throwing someone back to the login page mid-invoice.
    if (await refreshOnce(args, store, extraOptions)) {
      result = await rawBaseQuery(args, store, extraOptions);
    } else {
      // The refresh token is gone too. The session is genuinely over.
      store.dispatch(loggedOut());
    }
  }

  if (result.error) return { error: normaliseError(result.error) };
  return result;
}

/** Build "?a=1&b=2", skipping empty values. Arrays become comma-separated. */
export function query(params = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
